import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { logger } from '../utils/logger';
import BlockToolbox from '../components/StrategyBuilder/BlockToolbox';
import HypothesisIntakeLauncher from '../components/Validation/HypothesisIntakeLauncher';
import HypothesisWorkspaceLauncher from '../components/Hypothesis/HypothesisWorkspaceLauncher';
import BlockCanvas, { BlockCanvasRef } from '../components/StrategyBuilder/BlockCanvas';
import DiscoveryProvenanceBanner from '../components/Discovery/DiscoveryProvenanceBanner';
// P9.2A §16 — generation-profile lineage (structural_setup_v1 detail; legacy label otherwise)
import GenerationLineagePanel from '../components/StrategyBuilder/GenerationLineagePanel';
import StrategySettings from '../components/StrategyBuilder/StrategySettings';
import BlockEditor from '../components/StrategyBuilder/BlockEditor';
import AIStrategyBuilder from '../components/StrategyBuilder/AIStrategyBuilder';
import ValidationPanel from '../components/StrategyBuilder/ValidationPanel';
import TemplateBrowser from '../components/StrategyBuilder/TemplateBrowser';
import CreateTemplateDialog from '../components/StrategyBuilder/CreateTemplateDialog';
import ImportExportButtons from '../components/StrategyBuilder/ImportExportButtons';
import ScriptView from '../components/StrategyBuilder/ScriptView';
import ShareStrategyDialog from '../components/StrategyBuilder/ShareStrategyDialog';
import SaveStrategyDialog from '../components/StrategyBuilder/SaveStrategyDialog';
import { SaveActions } from '../components/StrategyBuilder/SaveActions';
import type { SaveActionKind } from '../components/StrategyBuilder/saveToolbarLayout';
import {
  Block,
  BlockType,
  ValidationResult,
  RiskMetrics,
  TradingState,
  TradingAlert,
  CollaborationUser,
  StrategyTemplate,
} from '../types';
import {
  Braces,
  Code,
  Book,
  Share2,
  Users,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Copy,
  Settings,
  Eye,
  Download,
  Bell,
  Brain,
  Upload,
  Globe,
  Lock,
} from 'lucide-react';
// S13 (2026-06-04) — Trading / Alerts / Analytics tabs removed.
// • Trading: redundant with the Backtest & Validation page.
// • Alerts:  redundant with the Signals page.
// • Analytics: was a placeholder ("would go here") — not shipping.
// Bell stays (toast notification renderer below still uses it).
// BarChart3 + TestTube dropped — only the dead tab buttons + the
// removed Analytics panel header used them.
// Settings — S14 hotfix (2026-06-04): S14 nuked the standalone
// "TSL Settings" modal + its toolbar button, but the lucide
// Settings icon is ALSO used at line ~1143 by the "Inputs &
// Properties" toolbar button as a generic gear glyph. Removing
// the import broke that JSX with a `Settings is not defined`
// ReferenceError that fired only after the page had blocks to
// render (the Inputs & Properties button is gated on
// strategyState.blocks.length > 0). tsc emitted no diagnostic
// because lib.dom.d.ts ships a `Settings` interface (Push Manager
// related) that TS happily resolved as the JSX element type —
// runtime, of course, sees no value bound to that name.
// The fix: re-import the icon. Audited the file: no other lucide
// icons have the same DOM-name shadowing risk here.
import { strategyValidator } from '../components/StrategyBuilder/strategyValidation';
import { createCollaborationService } from '../services/collaborationService';
import { useAuth } from '../contexts/AuthContext';
import { useStrategy } from '../contexts/StrategyContext';
// S60 (2026-06-06) — Mobile shell. When the user has opted into the
// mobile canvas via the App-level override, restructure the 'blocks'
// tab: collapsible palette accordion + framed canvas + bottom-sheet
// block editor, instead of the desktop side-by-side layout that
// pushes content off-screen on narrow viewports.
import { useIsMobile } from '../hooks/useIsMobile';
import { useBuilderMobileOverride } from '../hooks/useBuilderMobileOverride';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  useReplaceStrategyGuard,
  ReplaceStrategyConfirm,
} from '../components/Common/ReplaceStrategyConfirm';
import { updateTemplate, resolveActiveTemplate } from '../services/templateService';
import { canUpdateInPlace } from '../services/templateLifecycle';
// Slice H2 — curated starter-template gallery. Distinct from the
// existing Supabase-backed TemplateBrowser (community user-generated).
// The gallery imports from the leaf templates module so the modal
// can render without any network call.
import CuratedTemplateGallery from '../components/StrategyBuilder/CuratedTemplateGallery';
import { type CuratedTemplate } from '../templates';
import { track } from '../utils/analytics';
// Slice H2.1 — cross-page intent handshake. When an H1 suggestion on
// BacktestPanel / ConvertPage fires its "Browse starter templates" CTA,
// it sets a localStorage intent + navigates to #builder. This page
// consumes the intent on mount + opens the gallery with the recorded
// entry point.
import { consumePendingCuratedGalleryIntent } from '../services/templateGalleryIntent';
// S13 (2026-06-04) — UnifiedTradingPanel + AlertManager imports
// dropped along with the Trading + Alerts tabs that hosted them.
// The components themselves still exist under
// src/components/TradingView/ for any future re-use, but they are
// no longer wired into the Builder.

interface StrategyBuilderState {
  blocks: Block[];
  selectedBlock: Block | null;
  // S13 (2026-06-04) — narrowed from
  // 'blocks' | 'ai' | 'templates' | 'trading' | 'alerts' | 'analytics'.
  // The latter three tabs were redundant with Backtest & Validation
  // (trading) and the Signals page (alerts) or were a placeholder
  // (analytics). Narrowing the union means stale tab IDs from
  // older saved sessions trip a TypeScript discriminant check at
  // the setBuilderTab boundary, and we coerce to 'blocks' on load.
  builderTab: 'blocks' | 'ai' | 'templates';
  validationResult: ValidationResult | undefined;
  isExecuting: boolean;
  executionMode: 'paper' | 'live' | 'backtest';
  performance: {
    totalReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    winRate: number;
  };
  alerts: TradingAlert[];
  riskMetrics: RiskMetrics;
  
  // New unified trading state
  tradingState: TradingState;
}

// Full-cover overlay shown over the canvas while a persisted templateId is RESOLVING (skeleton) or after it
// FAILED/timed out (safe error). In neither case is the retired payload rendered underneath (the canvas is
// fed empty blocks while gated). The error state never falls back to the retired editable template.
const ResolveOverlay: React.FC<{ error: boolean; onRetry: () => void; onReturn: () => void }> = ({ error, onRetry, onReturn }) => (
  <div
    className="absolute inset-0 z-[60] flex items-center justify-center bg-white"
    data-testid={error ? 'builder-resolve-error' : 'builder-canvas-skeleton'}
  >
    {error ? (
      <div className="flex flex-col items-center gap-3 text-center max-w-sm px-4">
        <div className="text-sm font-semibold text-gray-900">We couldn’t load the current version of this template.</div>
        <div className="text-xs text-gray-500">The retired version was not loaded.</div>
        <div className="flex items-center gap-2 mt-1">
          <button type="button" onClick={onRetry} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded">Retry</button>
          <button type="button" onClick={onReturn} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">Return to Templates</button>
        </div>
      </div>
    ) : (
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <div className="text-sm">Loading strategy…</div>
      </div>
    )}
  </div>
);

interface UIState {
  showCreateTemplate: boolean;
  showShareDialog: boolean;
  showSaveDialog: boolean;
  // When true, the Save dialog forces a NEW copy ("Save as a copy" menu action) rather than update-in-place.
  saveAsCopy: boolean;
  showJsonPreview: boolean;
  showStrategyInputs: boolean;
  // S14 (2026-06-04) — TSL Settings modal removed. Dropped flags:
  //   • showSettings — modal visibility
  //   • showMinimap — toggled nothing (no Minimap component existed)
  //   • autoSave — debounced useEffect called handleAutoSave whose
  //     body was a `// Auto-save logic here` comment; stub plumbing
  //   • darkMode — toggled nothing (no .dark class application)
  //   • showAdvancedFeatures — zero readers anywhere
  // `notifications` retained but unconditional — the top-right toast
  // is always-on now.
  currentStrategyId: string | null;
  currentTemplateId: string | null;
  collaborators: CollaborationUser[];
  savingStrategy: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  showCopyDialog: boolean;
  copyingTemplate: boolean;
  copyMetadata: {
    name: string;
    description: string;
    isPublic: boolean;
  };
  currentTemplate: StrategyTemplate | null;
  templateResolving: boolean;      // resolving a persisted templateId (gate render; never show retired payload)
  templateResolveError: boolean;   // resolution failed/timed out → safe error state (never reveal retired payload)
  templateRedirectNotice: boolean; // toast: auto-redirected a retired template to its migrated successor
  isReadOnly: boolean;
  // S61 (2026-06-06) — Dedicated "Update template" path. Distinct
  // from the Save flow: this pushes the current canvas back to the
  // loaded template's row in strategy_templates (mutating the source
  // that downstream copy/share recipients will see). Gated on
  // ownership (currentTemplate.author.id === user.id) AND a confirm
  // dialog so the destructive-overwrite is intentional.
  showUpdateTemplateConfirm: boolean;
  updatingTemplate: boolean;
  updateTemplateError: string | null;
}

const StrategyBuilderPage: React.FC = () => {
  const { user } = useAuth();
  const strategyCtx = useStrategy();
  // S60 (2026-06-06) — `inMobileShell` is true iff the viewport is
  // mobile AND the user has opted into the canvas via the App-level
  // override. When true, the 'blocks' tab swaps its desktop side-by-
  // side layout for a vertical palette-accordion + framed canvas +
  // bottom-sheet block editor. All other tabs are list-based and
  // already render fine on mobile, so the shell is a no-op there.
  const isMobile = useIsMobile();
  const [builderMobileOverride] = useBuilderMobileOverride();
  const inMobileShell = isMobile && builderMobileOverride;
  // Palette accordion open/closed. Default closed on mobile so the
  // canvas is the first thing the user sees — opening the accordion
  // is a deliberate "I want to add a block" action.
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  // Replace-confirm guard: shared by the three load paths (AI generation,
  // Template apply, Import) so all three honour the same "don't destroy
  // unsaved work" promise. Renders nothing when nothing is pending.
  const replaceGuard = useReplaceStrategyGuard();

  // Slice H2 — curated starter-template gallery state. Distinct from
  // the existing 'templates' tab (which renders the user-generated
  // TemplateBrowser). The gallery modal is opened from two entry
  // points: empty-builder state (the canvas overlay) and the header
  // toolbar button.
  const [curatedGallery, setCuratedGallery] = useState<{
    open: boolean;
    entryPoint: 'empty-builder' | 'toolbar' | 'ai-optimize-seed' | 'h1-suggestion';
  }>({ open: false, entryPoint: 'toolbar' });
  const openCuratedGallery = useCallback(
    (entryPoint: 'empty-builder' | 'toolbar' | 'ai-optimize-seed' | 'h1-suggestion') => {
      setCuratedGallery({ open: true, entryPoint });
    },
    [],
  );
  const closeCuratedGallery = useCallback(() => {
    setCuratedGallery((prev) => ({ ...prev, open: false }));
  }, []);
  // Slice H2.1 — consume a pending "open gallery" intent on mount.
  // Set from off-page surfaces (BacktestPanel / ConvertPage H1
  // suggestion CTAs) before they navigate to #builder. Single-use:
  // the intent helper clears localStorage during consumption so a
  // page refresh doesn't re-open the gallery.
  //
  // Run once on mount — setCuratedGallery is a stable setState ref,
  // consumePendingCuratedGalleryIntent is a module-level function,
  // so the empty deps array is exhaustive-deps-correct.
  useEffect(() => {
    const pending = consumePendingCuratedGalleryIntent();
    if (pending) {
      setCuratedGallery({ open: true, entryPoint: pending });
    }
  }, []);
  // Curated-template load. Routes through `replaceGuard` so unsaved
  // work on the canvas isn't silently overwritten. Emits a
  // load_cancelled telemetry event if the replace-guard modal's
  // Cancel is clicked.
  const handleLoadCuratedTemplate = useCallback(
    (template: CuratedTemplate) => {
      // Close the gallery modal as soon as the user picks one — the
      // replace-guard dialog (when blocks.length > 0) takes over.
      closeCuratedGallery();
      const templateBlocks = template.blocks;
      const arrangedBlocks = arrangeBlocks(templateBlocks);
      replaceGuard.requestReplace({
        blocks: arrangedBlocks,
        name: template.name,
        source: 'template',
        label: template.name,
        meta: { curatedSlug: template.slug, curatedVersion: template.version },
        onComplete: () => {
          setStrategyState((prev) => ({
            ...prev,
            blocks: arrangedBlocks,
            builderTab: 'blocks',
          }));
        },
      });
    },
    // arrangeBlocks is declared below in the same component; React
    // hooks rules pass because it's stable via its own useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replaceGuard, closeCuratedGallery],
  );
  // Telemetry for the load_cancelled case — fires when the user
  // dismisses the replace-strategy confirmation dialog after picking
  // a curated template. We hook into replaceGuard.cancelReplace's
  // pending state: if the pending replacement carries our curated
  // marker AND the user cancels, emit the event.
  useEffect(() => {
    if (!replaceGuard.pending) return;
    const meta = replaceGuard.pending.meta as { curatedSlug?: string; curatedVersion?: string } | undefined;
    if (!meta?.curatedSlug) return;
    // The 'cancelled' state is observed by watching `pending` go from
    // non-null back to null WITHOUT the onComplete callback firing.
    // We rely on the replace-guard's existing semantics: confirm
    // fires onComplete; cancel just sets pending = null.
    // Capture a marker; the effect's cleanup fires the analytics
    // event only when the pending is reset without confirmation.
    let confirmed = false;
    const originalOnComplete = replaceGuard.pending.onComplete;
    replaceGuard.pending.onComplete = () => {
      confirmed = true;
      originalOnComplete?.();
    };
    return () => {
      if (!confirmed) {
        track('template_load_cancelled', {
          slug: meta.curatedSlug,
          version: meta.curatedVersion,
        });
      }
    };
  }, [replaceGuard.pending]);

  // Core state — blocks hydrate from the global StrategyContext so a strategy
  // loaded in another tab (or last session, via localStorage) is restored on
  // mount. Subsequent local edits push back to the context (see effects below).
  const [strategyState, setStrategyState] = useState<StrategyBuilderState>({
    blocks: strategyCtx.blocks,
    selectedBlock: null,
    builderTab: 'blocks',
    validationResult: undefined,
    isExecuting: false,
    executionMode: 'paper',
    performance: {
      totalReturn: 0,
      sharpeRatio: 0,
      maxDrawdown: 0,
      winRate: 0,
    },
    alerts: [],
    riskMetrics: {
      currentDrawdown: 0,
      maxDrawdown: 0,
      dailyVaR: 0,
      portfolioRisk: 0,
      concentrationRisk: 0,
      correlationRisk: 0,
      leverageRatio: 1,
    },
    tradingState: {
      symbol: '',
      timeframe: '',
      marketData: [],
      isDataLoaded: false,
      currentMode: 'backtest',
      replayPosition: 0,
      isReplayPlaying: false,
      replaySpeed: 1,
      startDate: '',
      endDate: '',
      initialBalance: 10000,
    },
  });

  // UI state
  const [uiState, setUIState] = useState<UIState>(() => ({
    // Gate the very first render: if a templateId is persisted (a template-backed session is about to be
    // restored), start in the resolving state so the canvas/banner/actions render a skeleton — never the
    // (possibly retired) persisted payload — until lifecycle resolution settles.
    templateResolving: !!(strategyCtx.meta?.templateId),
    templateResolveError: false,
    showCreateTemplate: false,
    showShareDialog: false,
    saveAsCopy: false,
    showSaveDialog: false,
    showJsonPreview: false,
    showStrategyInputs: false,
    currentStrategyId: null,
    currentTemplateId: null,
    collaborators: [],
    savingStrategy: false,
    saveError: null,
    saveSuccess: false,
    showCopyDialog: false,
    copyingTemplate: false,
    copyMetadata: { name: '', description: '', isPublic: false },
    currentTemplate: null,
    templateRedirectNotice: false,
    isReadOnly: false,
    showUpdateTemplateConfirm: false,
    updatingTemplate: false,
    updateTemplateError: null,
  }));

  // Refs
  // S14 (2026-06-04) — saveTimeoutRef removed alongside the
  // stub auto-save useEffect / handler.
  const executionIntervalRef = useRef<NodeJS.Timeout>();
  const blockCanvasRef = useRef<BlockCanvasRef>(null);

  // ─── Global strategy sync ────────────────────────────────────────────────
  // Two-way binding between local strategyState.blocks and the global
  // StrategyContext, so the canvas survives navigation + page reload.
  //
  // The reference-equality guards prevent feedback loops: when local pushes
  // to context, the next render's context.blocks === local.blocks so the
  // reverse effect short-circuits.

  // Push local edits to context.
  useEffect(() => {
    if (strategyState.blocks === strategyCtx.blocks) return;
    strategyCtx.patchBlocks(strategyState.blocks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyState.blocks]);

  // Pick up external context changes (other tabs, AI generator on a different page).
  useEffect(() => {
    if (strategyCtx.blocks === strategyState.blocks) return;
    setStrategyState(prev => ({ ...prev, blocks: strategyCtx.blocks }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyCtx.blocks]);

  // Execution mode handlers - DEFINE THESE IMMEDIATELY AFTER STATE.
  //
  // This used to be a 2-second `setTimeout` that "simulated" a backtest while
  // setting `isExecuting=true`. It's a no-op now: the Trading tab's actual
  // backtest runs inside UnifiedTradingPanel.handleExecutionStart, which
  // drives ClientSideBacktestEngine directly. This parent-level handler only
  // exists to track the global isExecuting UI flag for the page chrome.
  // Honest-positioning pass: the live-trading stub was removed. It was
  // a no-op that just logged a debug message, but its existence implied
  // the UI button could initiate a real trade. There is no live broker
  // integration yet; re-add when there's actually something to call.

  const updateExecutionMetrics = () => {
    setStrategyState((prev) => ({
      ...prev,
      performance: {
        ...prev.performance,
        // Update with real metrics
      },
    }));
  };

  // Memoized computations
  const strategyComplexity = useMemo(() => {
    return strategyState.validationResult?.performance?.complexity || 0;
  }, [strategyState.validationResult]);


  const handleStopExecution = useCallback(() => {
    setStrategyState(prev => ({ 
      ...prev, 
      isExecuting: false,
      tradingState: { ...prev.tradingState, isReplayPlaying: false }
    }));
  }, []);

  // (Per-render debug logs removed — they fire on every state change and
  //  flood the production console. Re-add behind logger.debug() only when
  //  actively investigating a specific compliance bug.)

  // Auto-validation with TSL compliance
  useEffect(() => {
    if (strategyState.blocks.length > 0) {
      const validation = strategyValidator.validateWithErrorHandling(
        strategyState.blocks
      );
      setStrategyState((prev) => ({ ...prev, validationResult: validation }));
    }
  }, [strategyState.blocks]);

  // S14 (2026-06-04) — Auto-save useEffect removed. Pre-S14 it
  // debounced 2 s after blocks changed and called handleAutoSave,
  // whose body was literally `// Auto-save logic here` with no
  // save call — so the cycle did nothing but allocate timeouts.
  // If we re-introduce auto-save, the right shape is a debounced
  // PUT to /api/strategies/:id keyed off the user's currently
  // selected strategy + a dirty flag from the SaveStrategyDialog
  // pipeline, NOT a blanket flag.

  // Strategy execution monitoring
  useEffect(() => {
    if (strategyState.isExecuting) {
      executionIntervalRef.current = setInterval(() => {
        updateExecutionMetrics();
      }, 1000);
    } else if (executionIntervalRef.current) {
      clearInterval(executionIntervalRef.current);
    }

    return () => {
      if (executionIntervalRef.current) {
        clearInterval(executionIntervalRef.current);
      }
    };
  }, [strategyState.isExecuting]);

  // Auto-hide success messages
  useEffect(() => {
    if (uiState.saveSuccess) {
      const timer = setTimeout(() => {
        setUIState((prev) => ({ ...prev, saveSuccess: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [uiState.saveSuccess]);

  // Real-time collaboration
  useEffect(() => {
    if (uiState.currentStrategyId) {
      const collaboration = createCollaborationService(
        uiState.currentStrategyId
      );

      const initializeCollaboration = async () => {
        try {
          await collaboration.initialize(strategyState.blocks);

          collaboration.subscribeToChanges((updatedBlocks) => {
            setStrategyState((prev) => ({
              ...prev,
              blocks: updatedBlocks,
              validationResult:
                strategyValidator.validateWithErrorHandling(updatedBlocks),
            }));
          });

          collaboration.subscribeToPresence((users) => {
            const collaborationUsers: CollaborationUser[] = users.map(user => ({
              ...user,
              lastActivity: new Date()
            }));
            setUIState((prev) => ({ ...prev, collaborators: collaborationUsers }));
          });
        } catch (error) {
          console.error('Failed to initialize collaboration:', error);
        }
      };

      initializeCollaboration();

      return () => {
        collaboration.destroy();
      };
    }
  }, [uiState.currentStrategyId]);

  // Ref indirection so the lifecycle-redirect effect (below) can call handleApplyTemplate, which is
  // declared further down the component — avoids a use-before-declaration TDZ in the effect's deps.
  const applyTemplateRef = useRef<((blocks: Block[], templateId?: string) => void) | null>(null);
  // Bumped by "Retry" in the resolve-error state to re-run the resolution effect.
  const [resolveNonce, setResolveNonce] = useState(0);
  // Retry resolving the current template id from the safe error state (stays gated until it settles).
  const retryTemplateResolve = useCallback(() => {
    setUIState((prev) => ({ ...prev, templateResolveError: false, templateResolving: true }));
    setResolveNonce((n) => n + 1);
  }, []);
  // Leave the error state safely: clear the (retired) canvas + the template link, then open the gallery.
  const returnToTemplatesFromError = useCallback(() => {
    applyTemplateRef.current?.([], undefined); // replace the retired canvas with an empty one (never render it)
    setUIState((prev) => ({ ...prev, templateResolveError: false, templateResolving: false, currentTemplateId: null, currentTemplate: null }));
  }, []);

  // Template details fetching + lifecycle redirect (migration 0074). If currentTemplateId resolves to a
  // RETIRED record with a verified migrated successor (e.g. a pre-TSL legacy copy), automatically load the
  // successor's native blocks, repair the persisted template id, and surface a neutral toast — so an old
  // bookmark / recent item / persisted builder state never reopens the retired JavaScript version, and a
  // refresh stays on the migrated one. Active templates load unchanged.
  useEffect(() => {
    const id = uiState.currentTemplateId;
    if (!id) {
      setUIState((prev) => ({ ...prev, currentTemplate: null }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { template, redirectedFrom, retiredUnresolvable } = await resolveActiveTemplate(id);
        if (cancelled) return;
        if (retiredUnresolvable || !template) {
          // retired record with no valid successor → safe error state; NEVER render the retired payload.
          setUIState((prev) => ({ ...prev, templateResolving: false, templateResolveError: true }));
          return;
        }
        if (redirectedFrom && template.id !== id) {
          // retired → successor: swap the legacy blocks for the migrated native ones (handleApplyTemplate
          // persists blocks + meta.templateId + currentTemplateId). KEEP templateResolving true — this effect
          // re-runs once on the successor id and settles below (the successor is validated active, so no
          // further redirect). The skeleton stays up the whole time ⇒ the retired payload never renders.
          applyTemplateRef.current?.(template.blocks, template.id);
          setUIState((prev) => ({ ...prev, currentTemplate: template, templateRedirectNotice: true }));
        } else {
          // settled on an active / legacy-no-successor / successor template → reveal it.
          setUIState((prev) => ({ ...prev, currentTemplate: template, templateResolving: false }));
        }
      } catch (err) {
        console.error('Error resolving template:', err);
        // FAIL SAFE — go to a safe ERROR state, NEVER reveal the retired payload. The canvas stays gated
        // (error overlay), the banner stays suppressed, and template actions stay hidden.
        if (!cancelled) setUIState((prev) => ({ ...prev, templateResolving: false, templateResolveError: true }));
      }
    })();
    return () => { cancelled = true; };
     
  }, [uiState.currentTemplateId, resolveNonce]);

  // Safety net: if resolution HANGS (slow/stuck network), do NOT reveal the (possibly retired) payload —
  // transition skeleton → safe error state after 15s. Retry / Return to Templates are the only ways out.
  useEffect(() => {
    if (!uiState.templateResolving) return;
    const t = setTimeout(() => setUIState((prev) => ({ ...prev, templateResolving: false, templateResolveError: true })), 15000);
    return () => clearTimeout(t);
  }, [uiState.templateResolving]);

  // Auto-dismiss the lifecycle redirect toast.
  useEffect(() => {
    if (!uiState.templateRedirectNotice) return;
    const t = setTimeout(() => setUIState((prev) => ({ ...prev, templateRedirectNotice: false })), 6000);
    return () => clearTimeout(t);
  }, [uiState.templateRedirectNotice]);

  // S86 — restore the loaded strategy's source template id from the persisted
  // StrategyContext meta. uiState.currentTemplateId is LOCAL and resets to null
  // on every Builder mount, but the context (and meta.templateId) persists
  // across navigation — so without this, loading a template then leaving and
  // returning drops the "Update Template" (in-place save) option, leaving only
  // "Save TSL Strategy" (Save As). Skip when a strategy-id save took precedence.
  // useLayoutEffect (not useEffect) so the restore + resolving gate are applied BEFORE the browser paints —
  // the persisted (possibly retired) payload never reaches the screen even for one frame.
  useLayoutEffect(() => {
    const metaTemplateId =
      (strategyCtx.meta?.templateId as string | undefined) ?? null;
    if (metaTemplateId && !uiState.currentTemplateId && !uiState.currentStrategyId) {
      setUIState((prev) => ({ ...prev, currentTemplateId: metaTemplateId, templateResolving: true }));
    } else if (!metaTemplateId && uiState.templateResolving) {
      setUIState((prev) => ({ ...prev, templateResolving: false })); // nothing to resolve → reveal
    }
  }, [strategyCtx.meta, uiState.currentTemplateId, uiState.currentStrategyId, uiState.templateResolving]);

  // Auto-arrange blocks when switching to blocks tab
  useEffect(() => {
    if (strategyState.builderTab === 'blocks' && strategyState.blocks.length > 0) {
      // Use a longer timeout to ensure the component is fully rendered
      const timer = setTimeout(() => {
        if (blockCanvasRef.current) {
          logger.debug('Auto-arranging blocks on tab switch...');
          blockCanvasRef.current.autoArrangeBlocks();
          // Only fit content when switching tabs, not when adding blocks
          blockCanvasRef.current.fitContent();
        }
      }, 300); // Increased timeout to 300ms

      return () => clearTimeout(timer);
    }
  }, [strategyState.builderTab]);

  // Auto-arrange blocks whenever a fresh strategy is LOADED.
  //
  // Triggers on every load pathway — template apply, AI generation,
  // import, OnboardingFlow's setStrategy, cloud-sync hydration from
  // another device — by watching the StrategyContext `loadedAt` +
  // `strategySource` signals. We skip when source is 'manual' so
  // ongoing edits never override the user's positioning, and dedupe
  // via a ref so the same load can't re-fire on unrelated re-renders.
  //
  // The 300 ms timeout matches the tab-switch effect above: gives the
  // canvas time to mount the new blocks before measuring them.
  const lastArrangedLoadAtRef = useRef<number>(0);
  useEffect(() => {
    if (strategyState.builderTab !== 'blocks') return;
    if (strategyState.blocks.length === 0) return;
    const source = strategyCtx.strategySource;
    const loadedAt = strategyCtx.loadedAt ?? 0;
    const isFreshLoad =
      source === 'template' || source === 'ai' ||
      source === 'imported' || source === 'saved' ||
      source === 'evidence-import';
    if (!isFreshLoad) return;
    if (loadedAt <= lastArrangedLoadAtRef.current) return;
    lastArrangedLoadAtRef.current = loadedAt;
    const timer = setTimeout(() => {
      if (!blockCanvasRef.current) return;
      logger.debug('Auto-arranging blocks on strategy load…', { source, loadedAt });
      blockCanvasRef.current.autoArrangeBlocks();
      blockCanvasRef.current.fitContent();
    }, 300);
    return () => clearTimeout(timer);
  }, [
    strategyState.builderTab,
    strategyState.blocks,
    strategyCtx.loadedAt,
    strategyCtx.strategySource,
  ]);

  // S87 — clear the block selection whenever a fresh strategy is LOADED
  // (template / AI / import / cloud-sync). Without this, the previously
  // selected block stays selected as a STALE reference into the now-replaced
  // strategy — and if it was a CUSTOM block, its full-screen TSL Custom Block
  // Editor keeps re-appearing on every template load (the user's report:
  // "this window kept showing up every time I tried to load a template").
  // Loading a new strategy should start with NO block selected so the right
  // panel + modal close cleanly. The isFreshLoad guard + dedupe ref mirror the
  // auto-arrange effect above: ongoing manual edits also bump loadedAt but set
  // source='manual', so they must NOT yank the user's active selection mid-edit.
  const lastDeselectLoadAtRef = useRef<number>(0);
  useEffect(() => {
    const source = strategyCtx.strategySource;
    const loadedAt = strategyCtx.loadedAt ?? 0;
    const isFreshLoad =
      source === 'template' || source === 'ai' ||
      source === 'imported' || source === 'saved' ||
      source === 'evidence-import';
    if (!isFreshLoad) return;
    if (loadedAt <= lastDeselectLoadAtRef.current) return;
    lastDeselectLoadAtRef.current = loadedAt;
    setStrategyState((prev) => (prev.selectedBlock ? { ...prev, selectedBlock: null } : prev));
  }, [strategyCtx.loadedAt, strategyCtx.strategySource]);

  // Event handlers
  const handleDragStart = useCallback(
    (blockType: BlockType, e: React.DragEvent) => {
      e.dataTransfer.setData('blockType', blockType);
    },
    []
  );

  const handleUpdateBlock = useCallback((updatedBlock: Block) => {
    try {
      setStrategyState((prev) => ({
        ...prev,
        blocks: prev.blocks.map((block) =>
          block.id === updatedBlock.id ? updatedBlock : block
        ),
        selectedBlock: updatedBlock,
      }));
    } catch (error) {
      console.error('Error updating block:', error);
      setUIState((prev) => ({
        ...prev,
        saveError: 'Failed to update block. Please try again.',
      }));
    }
  }, []);

  const handleSelectBlock = useCallback((block: Block) => {
    setStrategyState((prev) => ({ ...prev, selectedBlock: block }));
  }, []);

  const handleCloseEditor = useCallback(() => {
    setStrategyState((prev) => ({ ...prev, selectedBlock: null }));
  }, []);

  const handleStrategyGenerated = useCallback((generatedBlocks: Block[]) => {
    // Route through StrategyContext via the dirty-check guard so unsaved
    // edits on the canvas are protected. The local setStrategyState
    // mirror happens in onComplete so it only fires after the replace
    // actually happens (not on cancel).
    replaceGuard.requestReplace({
      blocks: generatedBlocks,
      name: 'AI-generated strategy',
      source: 'ai',
      label: 'AI-generated strategy',
      onComplete: () => {
        setStrategyState((prev) => ({
          ...prev,
          blocks: generatedBlocks,
          builderTab: 'blocks',
        }));
      },
    });
  }, [replaceGuard]);

  // Helper function to arrange blocks before setting them in state
  const arrangeBlocks = useCallback((blocks: Block[]): Block[] => {
    if (blocks.length === 0) return blocks;

    const arrangedBlocks = [...blocks];
    const horizontalSpacing = 380;
    const verticalSpacing = 180;
    const startX = 50;
    const startY = 50;

    // Handle Strategy Config block specially (top-left, isolated)
    const strategyConfigBlock = arrangedBlocks.find(
      (block) => block.type === BlockType.STRATEGY_CONFIG
    );

    if (strategyConfigBlock) {
      const strategyIndex = arrangedBlocks.findIndex(
        (b) => b.id === strategyConfigBlock.id
      );
      if (strategyIndex !== -1) {
        arrangedBlocks[strategyIndex] = {
          ...arrangedBlocks[strategyIndex],
          position: {
            x: startX,
            y: startY,
          },
        };
      }
    }

    // Find root blocks (excluding Strategy Config, PRICE_DATA, or blocks with no inputs)
    const rootBlocks = arrangedBlocks.filter(
      (block) =>
        block.type !== BlockType.STRATEGY_CONFIG &&
        (!block.connections.inputs || block.connections.inputs.length === 0)
    );

    if (rootBlocks.length === 0) {
      rootBlocks.push(
        ...arrangedBlocks.filter(
          (block) => block.type !== BlockType.STRATEGY_CONFIG
        )
      );
    }

    // Topological sort for TSL-compliant arrangement
    const visited = new Set<string>();
    const layers: string[][] = [];

    const dfs = (blockId: string, layer: number) => {
      if (visited.has(blockId)) return;
      visited.add(blockId);

      if (!layers[layer]) layers[layer] = [];
      layers[layer].push(blockId);

      const block = arrangedBlocks.find((b) => b.id === blockId);
      if (block?.connections.outputs) {
        block.connections.outputs.forEach((outputId) => {
          const outputBlock = arrangedBlocks.find((b) => b.id === outputId);
          if (outputBlock?.type !== BlockType.STRATEGY_CONFIG) {
            dfs(outputId, layer + 1);
          }
        });
      }
    };

    rootBlocks.forEach((rootBlock) => {
      if (rootBlock.type !== BlockType.STRATEGY_CONFIG) {
        dfs(rootBlock.id, 0);
      }
    });

    // Position blocks by layers (excluding Strategy Config)
    const strategyConfigOffset = strategyConfigBlock ? 250 : 0;

    layers.forEach((layer, layerIndex) => {
      layer.forEach((blockId, blockIndex) => {
        const block = arrangedBlocks.find((b) => b.id === blockId);
        if (block?.type === BlockType.STRATEGY_CONFIG) return;

        const blockIndex2 = arrangedBlocks.findIndex((b) => b.id === blockId);
        if (blockIndex2 !== -1) {
          arrangedBlocks[blockIndex2] = {
            ...arrangedBlocks[blockIndex2],
            position: {
              x: startX + layerIndex * horizontalSpacing,
              y: startY + strategyConfigOffset + blockIndex * verticalSpacing,
            },
          };
        }
      });
    });

    return arrangedBlocks;
  }, []);

  const handleApplyTemplate = useCallback(
    (templateBlocks: Block[], templateId?: string) => {
      // Local arrangeBlocks() gives a reasonable initial layout immediately;
      // the new auto-arrange-on-load effect below then runs the canvas's
      // canonical autoArrangeBlocks() (the same logic the toolbar button
      // uses) once the blocks have mounted, so every load path ends up
      // with the same visual result.
      const arrangedBlocks = arrangeBlocks(templateBlocks);

      // Route through the guard so an in-progress unsaved strategy on the
      // canvas isn't silently overwritten by a template apply.
      replaceGuard.requestReplace({
        blocks: arrangedBlocks,
        name: 'Loaded template',
        source: 'template',
        label: 'Loaded template',
        // S86 — persist the source template id in the StrategyContext meta so
        // the "Update Template" (in-place save) path survives a Builder remount
        // (navigate away → back). currentTemplateId below is LOCAL uiState and
        // resets on mount; the context meta persists (localStorage + cloud).
        meta: templateId ? { templateId } : undefined,
        onComplete: () => {
          setStrategyState((prev) => ({
            ...prev,
            blocks: arrangedBlocks,
            builderTab: 'blocks',
          }));
          if (templateId) {
            setUIState((prev) => ({
              ...prev,
              currentTemplateId: templateId,
              currentStrategyId: null,
            }));
          }
        },
      });
    },
    [arrangeBlocks, replaceGuard]
  );
  // Direct loader for the lifecycle redirect — bypasses the replace-guard (unsaved-work confirm). A
  // retired→successor redirect is a SYSTEM action, not a user-initiated replace; it must never pop a
  // confirm dialog (that would block the redirect and strand the skeleton until timeout). The retired
  // blocks can't run anyway, so there is no real work to protect. Persists blocks + meta.templateId so a
  // refresh stays on the successor. (handleApplyTemplate, which DOES use the guard, remains for explicit
  // user template loads.)
  const loadResolvedTemplate = useCallback((blocks: Block[], templateId?: string) => {
    const arranged = arrangeBlocks(blocks);
    // Drive the LOCAL canvas state (the two-way binding's source of truth) — it flows ONE way into the
    // StrategyContext via the binding (which persists). Setting the context directly here would race the
    // binding's reverse effect and let the stale (retired) local blocks overwrite the successor. patchMeta
    // persists the source template id so a refresh stays on the successor.
    setStrategyState((prev) => ({ ...prev, blocks: arranged, builderTab: 'blocks' }));
    strategyCtx.patchMeta(templateId ? { templateId } : { templateId: undefined });
    setUIState((prev) => ({ ...prev, currentTemplateId: templateId ?? null, currentStrategyId: null }));
  }, [arrangeBlocks, strategyCtx]);
  applyTemplateRef.current = loadResolvedTemplate;

  const handleImport = useCallback((importedBlocks: Block[]) => {
    // Same routing as template / AI: through StrategyContext via the
    // guard so the auto-arrange-on-load effect detects it via
    // `loadedAt` + source, AFTER unsaved-work confirmation if needed.
    replaceGuard.requestReplace({
      blocks: importedBlocks,
      name: 'Imported strategy',
      source: 'imported',
      label: 'Imported strategy',
      onComplete: () => {
        setStrategyState((prev) => ({ ...prev, blocks: importedBlocks }));
      },
    });
  }, [replaceGuard]);

  // S14 (2026-06-04) — handleAutoSave removed (was a stub —
  // see the auto-save useEffect removal note above).

  // Template and strategy management
  const handleTemplateCreated = useCallback((templateId: string) => {
    logger.debug('TSL Template created:', templateId);
    // S86 — persist the new template id so subsequent saves "Update Template"
    // it (in place) and the link survives a Builder remount.
    strategyCtx.patchMeta({ templateId });
    setUIState((prev) => ({
      ...prev,
      showCreateTemplate: false,
      currentTemplateId: templateId,
      saveSuccess: true,
    }));
  }, [strategyCtx]);

  const handleStrategySaved = useCallback((strategyId: string) => {
    setUIState((prev) => ({
      ...prev,
      currentStrategyId: strategyId,
      currentTemplateId: null,
      saveSuccess: true,
    }));
    logger.debug('TSL Strategy saved with ID:', strategyId);
  }, []);

  // S61 (2026-06-06) — Update the loaded template's source row with
  // the current canvas. Author-only (DB enforces via the
  // `.eq('author_id', user.id)` filter in updateTemplate; we ALSO
  // gate the UI button on ownership so non-authors never see the
  // option). The two-step confirm (toolbar button → modal → confirm)
  // is deliberate: this overwrites the canonical template, and any
  // copy/share downstream consumers will pick up the new shape on
  // their next load.
  const handleUpdateTemplate = useCallback(async () => {
    if (!uiState.currentTemplateId) return;
    setUIState((prev) => ({ ...prev, updatingTemplate: true, updateTemplateError: null }));
    try {
      const updated = await updateTemplate(uiState.currentTemplateId, {
        // Only the canvas. Metadata (name, description, tags, etc.)
        // edits go through the existing SaveStrategyDialog 'update'
        // mode — this fast-path button is specifically "push my new
        // block configuration to the template I already authored".
        blocks: strategyState.blocks,
      });
      setUIState((prev) => ({
        ...prev,
        updatingTemplate: false,
        showUpdateTemplateConfirm: false,
        currentTemplate: updated,
        saveSuccess: true,
      }));
      logger.debug('Template updated via S61 fast path:', uiState.currentTemplateId);
    } catch (err) {
      setUIState((prev) => ({
        ...prev,
        updatingTemplate: false,
        updateTemplateError: err instanceof Error ? err.message : 'Failed to update template',
      }));
    }
  }, [uiState.currentTemplateId, strategyState.blocks]);

  // S61 — Author check. Hide the Update template button when the
  // user is NOT the author so non-owners don't see a button that
  // would fail at the DB layer with a confusing "Template not found
  // or you do not have permission" error.
  const isTemplateAuthor = useMemo(() => {
    if (!user || !uiState.currentTemplate) return false;
    return uiState.currentTemplate.author?.id === user.id;
  }, [user, uiState.currentTemplate]);

  // Route a SaveActions primary/menu action to its existing dialog (presentation only — same backend).
  const runSaveAction = useCallback((kind: SaveActionKind) => {
    switch (kind) {
      case 'update-template':
        setUIState((prev) => ({ ...prev, showUpdateTemplateConfirm: true, updateTemplateError: null }));
        break;
      case 'save-as-copy':
        setUIState((prev) => ({ ...prev, showSaveDialog: true, saveAsCopy: true }));
        break;
      case 'publish-template':
        setUIState((prev) => ({ ...prev, showCreateTemplate: true }));
        break;
      case 'save-strategy':
      default:
        setUIState((prev) => ({ ...prev, showSaveDialog: true, saveAsCopy: false }));
        break;
    }
  }, []);

  // Generate strategy JSON
  const generateStrategyJSON = useCallback(() => {
    return JSON.stringify(
      {
        version: 'TSL_v1.0',
        blocks: strategyState.blocks,
        metadata: {
          created: new Date(),
          version: '1.0.0',
          complexity: strategyComplexity,
          validation: strategyState.validationResult,
          tslCompliant: strategyState.validationResult?.isValid || false,
        },
      },
      null,
      2
    );
  }, [
    strategyState.blocks,
    strategyComplexity,
    strategyState.validationResult,
  ]);

  // UI state setters
  const setBuilderTab = useCallback((tab: typeof strategyState.builderTab) => {
    setStrategyState((prev) => ({ ...prev, builderTab: tab }));
  }, []);

  const toggleJsonPreview = useCallback(() => {
    setUIState((prev) => ({ ...prev, showJsonPreview: !prev.showJsonPreview }));
  }, []);

  const toggleStrategyInputs = useCallback(() => {
    setUIState((prev) => ({ ...prev, showStrategyInputs: !prev.showStrategyInputs }));
  }, []);

  // S14 (2026-06-04) — toggleSettings removed alongside the TSL
  // Settings modal it opened.

  const handleUpdateBlocks = useCallback(
    (blocks: Block[] | ((prev: Block[]) => Block[])) => {
      setStrategyState((prev) => ({
        ...prev,
        blocks: typeof blocks === 'function' ? blocks(prev.blocks) : blocks,
      }));
    },
    []
  );

  // Fundamentals quick-add (flag-gated in BlockToolbox): append the preconfigured
  // EXPRESSION block and select it so its editor opens with the fundamental operand
  // already filled in. No new BlockType — identical to a manually-built expression.
  const handleQuickAddBlock = useCallback((block: Block) => {
    setStrategyState((prev) => ({ ...prev, blocks: [...prev.blocks, block], selectedBlock: block }));
  }, []);

  // Render tab content
  const renderTabContent = () => {
    switch (strategyState.builderTab) {
      case 'blocks': {
        // S60 (2026-06-06) — Mobile-shell branch. Stacks vertically so
        // the 320 px-wide BlockToolbox doesn't push the canvas off the
        // viewport. Palette is a collapsible accordion (closed by
        // default — canvas is the hero); canvas is a framed box of
        // fixed height; selected-block editor opens as a bottom sheet.
        // The desktop branch below is the unmodified pre-S60 layout.
        if (inMobileShell) {
          return (
            <div className="flex-1 flex flex-col overflow-y-auto bg-slate-50">
              {/* Palette accordion. Collapsed by default. Tapping the
                  header expands the BlockToolbox inline as a
                  full-width drawer; the toolbox's internal scrolling
                  caps it so it doesn't eat the whole viewport. */}
              <div className="bg-white border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setMobilePaletteOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800"
                  aria-expanded={mobilePaletteOpen}
                >
                  <span className="flex items-center gap-2">
                    <Braces className="w-4 h-4 text-indigo-600" />
                    Blocks palette
                    <span className="text-[11px] font-normal text-gray-500">
                      ({strategyState.blocks.length} on canvas)
                    </span>
                  </span>
                  {mobilePaletteOpen
                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                    : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </button>
                {mobilePaletteOpen && (
                  <div className="border-t border-gray-200 max-h-[50vh] overflow-y-auto">
                    {/* BlockToolbox sets its own w-80; we wrap in
                        full-width container and let inner scrolling
                        cap the height. The horizontal w-80 doesn't
                        cause overflow here because the parent
                        already has overflow-y-auto + the toolbox
                        scrolls within its own bounding box. */}
                    <BlockToolbox onDragStart={handleDragStart} onQuickAddBlock={handleQuickAddBlock} resolvingTemplate={uiState.templateResolving || uiState.templateResolveError} />
                  </div>
                )}
              </div>

              {/* Framed canvas — boxed in the center. Fixed-ish height
                  so it stays predictable on phones. */}
              <div className="p-3">
                {/* Discovery origin: visible when this graph was compiled from a
                    promoted Discovery candidate (data-driven; null otherwise). */}
                <DiscoveryProvenanceBanner
                  blocks={strategyState.blocks}
                  onOpenRun={() => { if (typeof window !== 'undefined') window.location.hash = '#discovery'; }}
                />
                {strategyState.blocks.length > 0 && <GenerationLineagePanel blocks={strategyState.blocks} />}
                <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{ height: '60vh' }}>
                  {(uiState.templateResolving || uiState.templateResolveError) && (
                    <ResolveOverlay error={uiState.templateResolveError} onRetry={retryTemplateResolve} onReturn={returnToTemplatesFromError} />
                  )}
                  <BlockCanvas
                    ref={blockCanvasRef}
                    blocks={uiState.templateResolving || uiState.templateResolveError ? [] : strategyState.blocks}
                    setBlocks={handleUpdateBlocks}
                    onSelectBlock={handleSelectBlock}
                  />
                  {strategyState.blocks.length === 0 && (
                    <div
                      className="absolute inset-0 flex items-center justify-center pointer-events-none p-3"
                      data-testid="h2-empty-builder-cta"
                    >
                      <div className="pointer-events-auto w-full max-w-sm text-center bg-white/95 backdrop-blur border border-fuchsia-200 rounded-lg shadow-md px-4 py-4">
                        <div className="text-sm font-semibold text-gray-900 mb-1">
                          Start with a template
                        </div>
                        <p className="text-[11px] text-gray-600 leading-relaxed mb-3">
                          Pick a curated starter strategy. Every one still
                          needs walk-forward validation on YOUR asset.
                        </p>
                        <button
                          type="button"
                          onClick={() => openCuratedGallery('empty-builder')}
                          data-testid="h2-empty-builder-open"
                          className="px-3 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-medium rounded transition-colors"
                        >
                          Browse starter templates
                        </button>
                        <div className="text-[10px] text-gray-500 mt-2">
                          Or open the Blocks palette above to drag a block in.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-gray-500 text-center">
                  Pinch-zoom the canvas; tap a block to edit it; drag from the palette to add.
                </p>
              </div>

              {/* Validation summary — inline below the canvas, NOT
                  as a side-panel (which would overlap on mobile). */}
              {strategyState.blocks.length > 0 &&
                strategyState.validationResult &&
                (strategyState.validationResult.errors.length > 0 ||
                 strategyState.validationResult.warnings.length > 0) && (
                <div className="px-3 pb-3">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <ValidationPanel validationResult={strategyState.validationResult} />
                  </div>
                </div>
              )}

              {/* Bottom-sheet block editor. Slides up over the canvas
                  when a block is tapped; tap the backdrop or the X
                  inside BlockEditor to close. Fixed-positioned so it
                  scrolls independently of the page. */}
              {strategyState.selectedBlock && (
                <>
                  <div
                    className="fixed inset-0 bg-black/30 z-30"
                    onClick={handleCloseEditor}
                    aria-hidden="true"
                  />
                  <div
                    className="fixed inset-x-0 bottom-0 z-40 bg-white rounded-t-xl shadow-2xl max-h-[85vh] overflow-y-auto"
                    role="dialog"
                    aria-label="Block editor"
                  >
                    {/* Pull-handle indicator so the sheet shape is
                        obvious without a chrome bar. */}
                    <div className="flex justify-center pt-2 pb-1">
                      <div className="w-10 h-1 bg-gray-300 rounded-full" />
                    </div>
                    <BlockEditor
                      selectedBlock={strategyState.selectedBlock}
                      onUpdate={handleUpdateBlock}
                      onClose={handleCloseEditor}
                      blocks={strategyState.blocks}
                      validationResult={strategyState.validationResult}
                      onSelectBlockId={(id) => {
                        const target = strategyState.blocks.find(b => b.id === id);
                        if (target) handleSelectBlock(target);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        }

        // Desktop layout (unchanged from pre-S60).
        return (
          <div className="flex-1 flex flex-col min-w-0">
            {/* Discovery origin banner — full width above the toolbox+canvas row;
                renders only when this graph came from a promoted Discovery candidate. */}
            <DiscoveryProvenanceBanner
              blocks={strategyState.blocks}
              onOpenRun={() => { if (typeof window !== 'undefined') window.location.hash = '#discovery'; }}
            />
            {strategyState.blocks.length > 0 && <GenerationLineagePanel blocks={strategyState.blocks} />}
          {/* min-h-0: without it this row's flex min-height:auto equals the toolbox's
              full content height (~3600px), so the row blows past the viewport, the
              toolbox's overflow-y-auto never engages (scrollHeight == clientHeight),
              and everything below the fold is clipped by the overflow-hidden ancestor
              with no way to scroll to it. */}
          <div className="flex-1 flex min-h-0">
            <BlockToolbox onDragStart={handleDragStart} onQuickAddBlock={handleQuickAddBlock} resolvingTemplate={uiState.templateResolving || uiState.templateResolveError} />
            {/* Validation panel — DOCKED as a flex column (not an absolute
                overlay) so it never renders on top of canvas blocks. The
                canvas flexes beside it; collapsing the panel (pill) hands the
                width back to the canvas. */}
            {strategyState.blocks.length > 0 &&
             strategyState.validationResult &&
             (strategyState.validationResult.errors.length > 0 ||
              strategyState.validationResult.warnings.length > 0) && (
              <div className="flex-shrink-0 border-r border-gray-200 overflow-hidden">
                <ValidationPanel validationResult={strategyState.validationResult} />
              </div>
            )}
            <div className="flex-1 relative min-w-0">
              {(uiState.templateResolving || uiState.templateResolveError) && (
                <ResolveOverlay error={uiState.templateResolveError} onRetry={retryTemplateResolve} onReturn={returnToTemplatesFromError} />
              )}
              <BlockCanvas
                ref={blockCanvasRef}
                blocks={uiState.templateResolving || uiState.templateResolveError ? [] : strategyState.blocks}
                setBlocks={handleUpdateBlocks}
                onSelectBlock={handleSelectBlock}
              />
              {/* Slice H2 — Empty-builder CTA. Renders when the canvas
                  has no blocks yet, offering a "Start from a template"
                  shortcut to the curated gallery. Distinct from the
                  community TemplateBrowser surface. */}
              {strategyState.blocks.length === 0 && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  data-testid="h2-empty-builder-cta"
                >
                  <div className="pointer-events-auto max-w-md text-center bg-white/95 backdrop-blur border border-fuchsia-200 rounded-lg shadow-md px-6 py-5">
                    <div className="text-base font-semibold text-gray-900 mb-1">
                      Start with a template
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mb-3">
                      Pick a curated starter strategy and load it into the canvas.
                      Templates are starting points, not guaranteed profitable
                      systems — every one still needs walk-forward validation
                      on YOUR asset.
                    </p>
                    <button
                      type="button"
                      onClick={() => openCuratedGallery('empty-builder')}
                      data-testid="h2-empty-builder-open"
                      className="px-4 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium rounded transition-colors"
                    >
                      Browse starter templates
                    </button>
                    <div className="text-[10px] text-gray-500 mt-3">
                      Prefer to build from scratch? Drag blocks from the toolbox on the left.
                    </div>
                    {/* Validation Engine (flag-gated, default off): start from a
                        testable hypothesis before building. Renders null when off. */}
                    <HypothesisIntakeLauncher />
                    {/* Hypothesis Engine workspace (its own flag, default off):
                        the conversation-first, axis-teaching intake. Null when off. */}
                    <HypothesisWorkspaceLauncher />
                  </div>
                </div>
              )}
              {strategyState.selectedBlock && (
                <div className="absolute top-0 right-0 h-full z-10">
                  <BlockEditor
                    selectedBlock={strategyState.selectedBlock}
                    onUpdate={handleUpdateBlock}
                    onClose={handleCloseEditor}
                    blocks={strategyState.blocks}
                    validationResult={strategyState.validationResult}
                    // S8d (2026-06-03) — Dependencies panel navigation.
                    // When the user clicks a row in the BlockEditor's
                    // Dependencies panel, jump-select the referenced
                    // block. Safe fallback: ignore the click if the id
                    // no longer resolves (stale references render as
                    // non-clickable inside the panel itself, so this
                    // guard is belt-and-suspenders).
                    onSelectBlockId={(id) => {
                      const target = strategyState.blocks.find(b => b.id === id);
                      if (target) handleSelectBlock(target);
                    }}
                  />
                </div>
              )}
              {/* (Validation panel is now docked as a flex column above —
                  it no longer overlays the canvas.) */}
            </div>
          </div>
          </div>
        );
      }

      case 'templates':
        return (
          <div className="flex-1 overflow-y-auto">
            <TemplateBrowser onApplyTemplate={handleApplyTemplate} />
          </div>
        );

      case 'ai':
        return (
          // Same overflow-y-auto wrapper pattern as the 'templates' case.
          // Without it the AIStrategyBuilder's flex-1 container had no
          // scrollable parent and the Example Prompts section was clipped
          // below the viewport with no way to reach it.
          <div className="flex-1 overflow-y-auto">
            <AIStrategyBuilder onStrategyGenerated={handleStrategyGenerated} />
          </div>
        );

      // S13 (2026-06-04) — Trading / Alerts / Analytics cases removed.
      // Trading was redundant with Backtest & Validation; Alerts with
      // the Signals page; Analytics was a "would go here" placeholder.
      // The default branch below now also coerces any stale builderTab
      // value (e.g. from a saved session pre-S13) to the blocks tab.

      default:
        return null;
    }
  };

  // S13 (2026-06-04) — Removed:
  //   • handleTradingStateChange — only consumed by the Trading tab's
  //     UnifiedTradingPanel; tradingState writes for replay-mode still
  //     happen inline in handleStartExecution / handleStopExecution
  //     (which remain — they're for the canvas replay button, not the
  //     deleted tab).
  //   • handleAlertCreate / handleAlertUpdate / handleAlertDelete —
  //     only consumed by the Alerts tab's AlertManager. The
  //     strategyState.alerts array stays in shape so the existing
  //     notification toast (Bell, top-right) keeps rendering, but no
  //     UI surface in the Builder mutates it any more. If alert
  //     authoring comes back, hang it on the Signals page where the
  //     user expects to find it.
  //   • renderAnalyticsPanel — was a placeholder with "visualization
  //     would go here" copy.

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Confirm-before-replace dialog — shared by AI generate / template
          apply / import load paths. Renders nothing when nothing's pending. */}
      <ReplaceStrategyConfirm
        pending={replaceGuard.pending}
        onConfirm={replaceGuard.confirmReplace}
        onCancel={replaceGuard.cancelReplace}
        onSaveFirst={() => {
          // Keep the current work: dismiss the guard and open the Save As dialog. After saving, the
          // strategy is no longer dirty, so the next New/Import/Template proceeds without the warning.
          replaceGuard.cancelReplace();
          setUIState((prev) => ({ ...prev, showSaveDialog: true }));
        }}
      />
      {/* Slice H2 — Curated starter-template gallery. Renders when
          opened from the empty-builder CTA or the toolbar button.
          Loading routes through `handleLoadCuratedTemplate` which
          wraps via `replaceGuard` so unsaved work on the canvas
          isn't silently overwritten. */}
      <CuratedTemplateGallery
        isOpen={curatedGallery.open}
        entryPoint={curatedGallery.entryPoint}
        onLoad={handleLoadCuratedTemplate}
        onClose={closeCuratedGallery}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="container mx-auto flex flex-wrap justify-between items-center">
          {/* Tab navigation.
              S62 (2026-06-06) — Horizontally scrollable strip on
              mobile. Pre-S62 the `flex space-x-4` row had no overflow
              treatment, so on narrow viewports "Starter templates" +
              "AI Assistant" were clipped past the right edge with no
              way to reach them. Now:
                • overflow-x-auto    — pan to reach off-screen tabs
                • -mx-4 px-4         — extends the scrollable area
                                       under the parent's px-4 so the
                                       last tab can fully clear the
                                       edge before snapping
                • shrink-0 whitespace-nowrap on each button — refuse
                  to be squashed; full label always visible while panning
                • scrollbar-thin / hide — looks native on mobile,
                  doesn't add a chrome scrollbar on desktop where the
                  row never overflows anyway */}
          <div className="flex space-x-2 overflow-x-auto -mx-4 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <button
              className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                strategyState.builderTab === 'blocks'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setBuilderTab('blocks')}
            >
              <Braces className="w-4 h-4" />
              <span>TSL Strategy Builder</span>
            </button>

            <button
              className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                strategyState.builderTab === 'templates'
                  ? 'bg-green-100 text-green-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setBuilderTab('templates')}
            >
              <Book className="w-4 h-4" />
              <span>Templates</span>
            </button>

            {/* Slice H2 — Curated starter-template gallery entry point.
                Distinct from the "Templates" tab above (community user-
                generated). Opens the modal regardless of which tab the
                user is currently on. */}
            <button
              type="button"
              className="shrink-0 whitespace-nowrap px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 text-fuchsia-700 hover:bg-fuchsia-50 border border-fuchsia-200"
              onClick={() => openCuratedGallery('toolbar')}
              data-testid="curated-gallery-toolbar-button"
              title="Open the curated starter-template gallery"
            >
              <Book className="w-4 h-4" />
              <span>Starter templates</span>
            </button>

            <button
              className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                strategyState.builderTab === 'ai'
                  ? 'bg-purple-100 text-purple-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setBuilderTab('ai')}
            >
              <Brain className="w-4 h-4" />
              <span>AI Assistant</span>
            </button>

            {/*
              S13 (2026-06-04) — Trading / Alerts / Analytics tab
              buttons removed. Trading lived alongside the dedicated
              Backtest & Validation page (redundant); Alerts lived
              alongside the dedicated Signals page (redundant);
              Analytics was a "would go here" placeholder. The Builder
              page's job is authoring + the AI / templates helpers —
              not hosting execution surfaces.
            */}
          </div>

          {/* Header controls.
              S63 (2026-06-06) — Whole controls row becomes a single
              horizontally-scrollable strip on mobile, matching the
              tabs row above. The previous S62 attempt only made the
              inner action-buttons div scrollable, but its parent (this
              flex container) capped the width and the `-mx-4 px-4`
              edge-extension couldn't push past the parent's bounds.
              Promoting the scroll to THIS level + dropping the inner
              overflow lets the whole strip (validation chip + action
              buttons) pan together, with the rightmost button (Save)
              fully clearing the screen edge before snapping back.
              `gap-4` replaces `space-x-4` so flex `gap` survives the
              scroll-container restyle cleanly. */}
          <div className="flex items-center gap-4 overflow-x-auto -mx-4 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0 [&_button]:whitespace-nowrap">
            {/* Collaborators */}
            {uiState.collaborators.length > 0 && (
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4 text-gray-500" />
                {uiState.collaborators.slice(0, 3).map((user, index) => (
                  <div
                    key={user.id || index}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: user.color }}
                    title={user.name}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                ))}
                {uiState.collaborators.length > 3 && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium">
                    +{uiState.collaborators.length - 3}
                  </div>
                )}
              </div>
            )}

            {/* TSL Validation status — hidden on an empty canvas. A pristine
                "New" strategy (0 blocks) isn't "invalid", it's just empty, so
                it must not surface the "N TSL errors" chip. */}
            {strategyState.validationResult && strategyState.blocks.length > 0 && (
              <div className="flex items-center space-x-2">
                {strategyState.validationResult.isValid ? (
                  <div className="flex items-center text-green-600">
                    <Check className="w-4 h-4 mr-1" />
                    <span className="text-sm">TSL Valid</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    <span className="text-sm">
                      {strategyState.validationResult.errors.length} TSL errors
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons.
                S63 (2026-06-06) — Overflow handling moved up to the
                parent controls strip so the validation chip pans with
                the buttons (one unit, edge-to-edge). This div just
                spaces its own children now. */}
            <div className="flex space-x-2">
              <ImportExportButtons
                blocks={strategyState.blocks}
                onImport={handleImport}
              />

              {/* P12 - Strategy Script authoring (compiles to the graph;
                  same replace flow as import, so validation + auto-arrange
                  + unsaved-work guard all apply). */}
              <ScriptView blocks={strategyState.blocks} onApply={handleImport} />

              {strategyState.blocks.length > 0 && (
                <>
                  <button
                    onClick={() => {
                      // Route through the shared replace-guard: clears instantly if there's nothing unsaved,
                      // otherwise shows the confirm modal (Save current first / Discard & start new / Cancel).
                      replaceGuard.requestReplace({
                        blocks: [], name: null, source: null, kind: 'new',
                        label: 'a new, empty strategy', confirmLabel: 'Discard & start new',
                        onComplete: () => setStrategyState((prev) => ({ ...prev, blocks: [] })),
                      });
                    }}
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
                    title="New strategy — clear the canvas and start fresh (does not delete any saved template)"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    New
                  </button>

                  {/* "Template" / "Publish as template" now lives in the Save ▾ menu (SaveActions). */}

                  <button
                    onClick={() =>
                      setUIState((prev) => ({ ...prev, showShareDialog: true }))
                    }
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" />
                    Share
                  </button>

                  <button
                    onClick={toggleStrategyInputs}
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
                    title="Inputs & Properties — tune the strategy's parameters and engine settings"
                  >
                    <Settings className="w-4 h-4 mr-1.5" />
                    {uiState.showStrategyInputs ? 'Hide Inputs' : 'Inputs & Properties'}
                  </button>

                  <button
                    onClick={toggleJsonPreview}
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Code className="w-4 h-4 mr-1.5" />
                    {uiState.showJsonPreview ? 'Hide JSON' : 'Show JSON'}
                  </button>

                </>
              )}

              {/* S14 (2026-06-04) — Settings toolbar button removed.
                  It opened the TSL Settings modal, whose 5 toggles
                  were: 3 dead (Dark Mode, Show Minimap, Advanced
                  Features — zero readers / no implementation), 1
                  stub (Auto-save — handler body was a comment), and
                  1 live (Notifications, now unconditional). */}

              {/* Decluttered SAVE cluster: one primary action chosen by ownership + a "Save ▾" menu
                  (Save as a copy / Publish as template). Update-template is owner-only. */}
              {uiState.templateRedirectNotice && (
                <div
                  className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 rounded-md bg-gray-900 text-white text-sm px-4 py-2.5 shadow-lg"
                  role="status"
                  data-testid="template-redirect-toast"
                >
                  <span>Opened the current TSL version. The legacy JavaScript version has been retired.</span>
                  <button
                    type="button"
                    onClick={() => setUIState((p) => ({ ...p, templateRedirectNotice: false }))}
                    className="text-gray-300 hover:text-white"
                    aria-label="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              )}
              {/* HIDE (not just disable) all template-derived save actions while resolving / on resolve
                  error — no "Update template" or other retired-record action is rendered until we've
                  settled on an active template. */}
              {!(uiState.templateResolving || uiState.templateResolveError) && (
                <SaveActions
                  state={{
                    // A retired template is never updatable in place (no overwriting/republishing the retired
                    // JS version) — treat it as "no loaded template" so the action falls to Save as a copy /
                    // Save strategy. In practice the builder redirects retired→successor on load, so the
                    // current template is active; this is a defensive guard.
                    hasTemplate: !!uiState.currentTemplateId && canUpdateInPlace(uiState.currentTemplate),
                    isTemplateAuthor,
                    hasStrategyId: !!uiState.currentStrategyId,
                  }}
                  disabled={
                    strategyState.blocks.length === 0 ||
                    uiState.savingStrategy ||
                    uiState.copyingTemplate
                  }
                  busyLabel={
                    uiState.savingStrategy ? 'Saving…'
                    : uiState.copyingTemplate ? 'Creating copy…'
                    : null
                  }
                  onAction={runSaveAction}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">{renderTabContent()}</div>

      {/* Strategy Inputs & Properties panel (persists into the TSL graph) */}
      {strategyState.blocks.length > 0 &&
        strategyState.builderTab === 'blocks' &&
        uiState.showStrategyInputs && (
          <div className="bg-gray-50 border-t border-gray-200 overflow-auto" style={{ maxHeight: '45vh' }}>
            <div className="max-w-3xl mx-auto p-3">
              <StrategySettings blocks={strategyState.blocks} defaultTab="inputs" />
            </div>
          </div>
        )}

      {/* TSL JSON preview panel */}
      {strategyState.blocks.length > 0 &&
        strategyState.builderTab === 'blocks' &&
        uiState.showJsonPreview && (
          <div
            className="bg-gray-900 text-gray-200 p-4 overflow-auto border-t border-gray-200"
            style={{ maxHeight: '250px' }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-400">
                TSL Strategy JSON Preview
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(generateStrategyJSON())
                  }
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                >
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([generateStrategyJSON()], {
                      type: 'application/json',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'tsl-strategy.json';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="text-xs text-green-400 hover:text-green-300 flex items-center space-x-1"
                >
                  <Download className="w-3 h-3" />
                  <span>Download</span>
                </button>
              </div>
            </div>
            <pre className="text-xs overflow-auto">
              {generateStrategyJSON()}
            </pre>
          </div>
        )}

      {/* S14 (2026-06-04) — TSL Settings modal removed. Audit found
          3 dead checkboxes (Dark Mode, Show Minimap, Advanced
          Features — zero readers / no implementation), 1 stub
          (Auto-save — handler body was a comment), and 1 live
          (Enable Notifications — now unconditional). */}

      {/* Dialogs remain the same */}
      {uiState.showCreateTemplate && (
        <CreateTemplateDialog
          blocks={strategyState.blocks}
          onClose={() =>
            setUIState((prev) => ({ ...prev, showCreateTemplate: false }))
          }
          onCreated={handleTemplateCreated}
        />
      )}

      {/* S61 (2026-06-06) — Update Template confirmation. Renders
          when the user has clicked the "Update template" toolbar
          button. Explicit overwrite warning + visibility hint
          (public templates affect every downstream copy / share
          recipient) so the destructive action is intentional. */}
      {uiState.showUpdateTemplateConfirm && uiState.currentTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-amber-600" />
                Update template?
              </h3>
              <button
                onClick={() =>
                  setUIState((prev) => ({ ...prev, showUpdateTemplateConfirm: false, updateTemplateError: null }))
                }
                disabled={uiState.updatingTemplate}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                <div className="flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1">This overwrites the template's saved blocks.</p>
                    <p className="text-xs leading-relaxed">
                      You're replacing the canvas stored in{' '}
                      <span className="font-semibold">{uiState.currentTemplate.name}</span> with
                      the {strategyState.blocks.length}-block configuration currently on screen.
                      This cannot be undone — the previous version is gone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-600 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span>Template</span>
                  <span className="font-mono text-gray-800 truncate ml-2">{uiState.currentTemplate.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Visibility</span>
                  <span className="font-medium inline-flex items-center">
                    {uiState.currentTemplate.isPublic ? (
                      <>
                        <Globe className="w-3 h-3 mr-1 text-emerald-600" />
                        <span className="text-emerald-700">Public — downstream users see the change on next load</span>
                      </>
                    ) : (
                      <>
                        <Lock className="w-3 h-3 mr-1 text-gray-500" />
                        <span className="text-gray-700">Private — only you can see this template</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Last updated</span>
                  <span className="text-gray-800">
                    {uiState.currentTemplate.updated
                      ? new Date(uiState.currentTemplate.updated).toLocaleString()
                      : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>New block count</span>
                  <span className="text-gray-800">{strategyState.blocks.length}</span>
                </div>
              </div>
              {uiState.updateTemplateError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 mt-0.5" />
                  <span>{uiState.updateTemplateError}</span>
                </div>
              )}
              <p className="text-[11px] text-gray-500">
                Tip: if you'd rather keep the original intact, use{' '}
                <span className="font-medium">Template</span> or <span className="font-medium">Save TSL Strategy</span> to make a copy instead.
              </p>
              <div className="pt-1 flex justify-end space-x-2">
                <button
                  onClick={() =>
                    setUIState((prev) => ({ ...prev, showUpdateTemplateConfirm: false, updateTemplateError: null }))
                  }
                  disabled={uiState.updatingTemplate}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTemplate}
                  disabled={uiState.updatingTemplate}
                  className="flex items-center px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded disabled:opacity-50"
                >
                  {uiState.updatingTemplate ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1.5" />
                      Update template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {uiState.showSaveDialog && (
        <SaveStrategyDialog
          blocks={strategyState.blocks}
          onClose={() =>
            setUIState((prev) => ({ ...prev, showSaveDialog: false, saveAsCopy: false }))
          }
          onSaved={handleStrategySaved}
          strategyId={uiState.currentStrategyId || undefined}
          templateId={uiState.currentTemplateId || undefined}
          // "Save as a copy" (menu / non-owned-template primary) forces a NEW record. The plain "Save
          // strategy" primary (personal strategy) updates/creates in place. "Update template" is its own
          // button, not this dialog.
          forceSaveAs={uiState.saveAsCopy}
        />
      )}

      {uiState.showShareDialog && uiState.currentStrategyId && (
        <ShareStrategyDialog
          strategyId={uiState.currentStrategyId}
          onClose={() =>
            setUIState((prev) => ({ ...prev, showShareDialog: false }))
          }
          onShared={() => {
            setUIState((prev) => ({
              ...prev,
              showShareDialog: false,
              saveSuccess: true,
            }));
          }}
        />
      )}

      {/* TSL Copy Template Dialog */}
      {uiState.showCopyDialog && uiState.currentTemplateId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-800">
                Create TSL Template Copy
              </h3>
              <button
                onClick={() =>
                  setUIState((prev) => ({ ...prev, showCopyDialog: false }))
                }
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-gray-600 mb-4">
                Create your own copy of this TSL template that you can modify
                and customize.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={uiState.copyMetadata.name}
                    onChange={(e) =>
                      setUIState((prev) => ({
                        ...prev,
                        copyMetadata: {
                          ...prev.copyMetadata,
                          name: e.target.value,
                        },
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Copy of TSL Template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uiState.copyMetadata.description}
                    onChange={(e) =>
                      setUIState((prev) => ({
                        ...prev,
                        copyMetadata: {
                          ...prev.copyMetadata,
                          description: e.target.value,
                        },
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={uiState.copyMetadata.isPublic}
                    onChange={(e) =>
                      setUIState((prev) => ({
                        ...prev,
                        copyMetadata: {
                          ...prev.copyMetadata,
                          isPublic: e.target.checked,
                        },
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="isPublic"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Make this TSL template public
                  </label>
                </div>
              </div>

              {uiState.saveError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {uiState.saveError}
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() =>
                    setUIState((prev) => ({ ...prev, showCopyDialog: false }))
                  }
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setUIState((prev) => ({ ...prev, copyingTemplate: true }));
                    try {
                      logger.debug('Copying TSL template...');
                      setUIState((prev) => ({
                        ...prev,
                        showCopyDialog: false,
                        copyingTemplate: false,
                        saveSuccess: true,
                      }));
                    } catch {
                      setUIState((prev) => ({
                        ...prev,
                        copyingTemplate: false,
                        saveError: 'Failed to copy TSL template',
                      }));
                    }
                  }}
                  disabled={uiState.copyingTemplate}
                  className={`flex items-center px-4 py-2 rounded-md text-white font-medium transition-colors ${
                    uiState.copyingTemplate
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {uiState.copyingTemplate ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1.5" />
                      Create a Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {uiState.saveError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-md max-w-sm">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">TSL Error</p>
              <p className="text-sm">{uiState.saveError}</p>
            </div>
            <button
              onClick={() =>
                setUIState((prev) => ({ ...prev, saveError: null }))
              }
              className="ml-4 text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {uiState.saveSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-md max-w-sm">
          <div className="flex items-start">
            <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">TSL Success</p>
              <p className="text-sm">
                {uiState.currentTemplateId
                  ? 'TSL Template updated successfully'
                  : 'TSL Strategy saved successfully'}
              </p>
            </div>
            <button
              onClick={() =>
                setUIState((prev) => ({ ...prev, saveSuccess: false }))
              }
              className="ml-4 text-green-400 hover:text-green-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* TSL alerts emitted during backtest. Routed through the
          alert-deliver edge function when a webhook URL / email is
          configured on the relevant terminal block.
          S14 (2026-06-04) — `uiState.notifications` gating dropped
          along with the TSL Settings modal. The toast is always-on
          now; the array is empty unless a real backtest emitted
          alerts, so always-on is fine. */}
      {strategyState.alerts.length > 0 && (
        <div className="fixed top-20 right-4 space-y-2 z-40">
          {strategyState.alerts.slice(0, 3).map((alert, _index) => (
            <div
              key={alert.id}
              className="p-3 rounded-lg shadow-lg max-w-sm bg-blue-100 border border-blue-400 text-blue-700"
            >
              <div className="flex items-start">
                <Bell className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium capitalize">TSL {alert.type}</p>
                  <p className="text-sm">{alert.description || alert.name}</p>
                  <p className="text-xs opacity-75 mt-1">
                    {alert.createdAt.toLocaleTimeString()}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setStrategyState((prev) => ({
                      ...prev,
                      alerts: prev.alerts.filter((a) => a.id !== alert.id),
                    }));
                  }}
                  className="ml-2 opacity-75 hover:opacity-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}

          {strategyState.alerts.length > 3 && (
            <div className="text-center">
              <button className="text-xs text-gray-500 hover:text-gray-700">
                +{strategyState.alerts.length - 3} more TSL alerts
              </button>
            </div>
          )}
        </div>
      )}

      {/* TSL Execution status overlay */}
      {strategyState.isExecuting && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-40">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              TSL Strategy is running in {strategyState.executionMode} mode
            </span>
            <button
              onClick={handleStopExecution}
              className="ml-4 text-green-200 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* TSL Read-only mode indicator */}
      {uiState.isReadOnly && (
        <div className="fixed bottom-4 left-4 bg-orange-100 border border-orange-400 text-orange-700 px-3 py-2 rounded shadow-md">
          <div className="flex items-center">
            <Eye className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">TSL Read-only mode</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyBuilderPage;
